import { useParams, Link } from 'react-router-dom';
import { comics, categories, chapters, reviews } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-book-2-line text-4xl"></i>
        </div>
        <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">
          漫画未找到
        </h1>
        <p className="text-foreground-500 mb-6">抱歉，您访问的漫画不存在或已下架。</p>
        <Link
          to="/"
          className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const comicChapters = chapters.filter((ch) => ch.comicId === comic.id);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫网在线漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-15',
    dateModified: comic.updated,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
    },
    image: comic.cover,
  };

  const comicSeriesSchema = {
    '@context': 'https://schema.org',
    '@type': 'BookSeries',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    numberOfPages: comic.chapters,
    inLanguage: 'zh-CN',
    genre: categories.find((c) => c.slug === comic.category)?.name || comic.category,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(comicSeriesSchema)}</script>

      <section className="bg-background-100/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类浏览', path: '/category/all' },
              {
                label: categories.find((c) => c.slug === comic.category)?.name || comic.category,
                path: `/category/${comic.category}`,
              },
              { label: comic.title },
            ]}
          />
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:w-[300px] flex-shrink-0">
            <div className="rounded-lg overflow-hidden sticky top-24">
              <img
                src={comic.cover}
                alt={`${comic.title} - 韩国漫画`}
                title={`${comic.title} - 韩国漫画在线阅读`}
                className="w-full aspect-[3/4] object-cover object-top"
                width="800"
                height="1100"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-3">
              <span className="text-sm text-foreground-500">
                作者：<strong className="text-foreground-800 font-medium">{comic.author}</strong>
              </span>
              <span className="flex items-center gap-1 text-sm text-accent-500">
                <i className="ri-star-fill"></i>
                <strong className="font-semibold">{comic.rating}</strong>
              </span>
              <span className="text-sm text-foreground-500">
                {formatViews(comic.views)} 次阅读
              </span>
              <span className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{comic.chapters}</strong> 话
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-label ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}
              >
                {comic.status}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 font-label"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button className="px-8 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-label font-semibold transition-colors whitespace-nowrap cursor-pointer">
                从第1话开始阅读
              </button>
              <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-bookmark-line mr-1"></i> 收藏
              </button>
              <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-share-forward-line mr-1"></i> 分享
              </button>
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">
                作品简介
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {comic.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
            章节列表（共 {comic.chapters} 话）
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto pr-2">
            {comicChapters.length > 0 ? (
              comicChapters.map((ch) => (
                <div
                  key={ch.number}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-label font-semibold">
                      {ch.number}
                    </span>
                    <div>
                      <p className="text-sm font-label text-foreground-800 group-hover:text-primary-600 transition-colors">
                        第{ch.number}话
                      </p>
                      <p className="text-xs text-foreground-400">{ch.title}</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-foreground-400">{ch.date}</span>
                </div>
              ))
            ) : (
              <p className="col-span-full text-center text-foreground-400 py-8">
                暂无章节信息
              </p>
            )}
          </div>
        </div>
      </section>

      {relatedComics.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">
            相关推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {relatedComics.map((c) => (
              <ComicCard
                key={c.id}
                {...c}
                categoryName={categories.find((cat) => cat.slug === c.category)?.name}
              />
            ))}
          </div>
        </section>
      )}
    </>
  );
}